<template>
	<v-form ref="form" @submit.prevent>
		<!-- header: what this module is, plus the actions for the whole card -->
		<div class="flex flex-row items-center gap-3 px-3 py-4 border-b">
			<span
				class="flex-grow-0 flex items-center justify-center w-8 h-8 rounded-full bg-blue-600 text-white font-medium">{{
					position }}</span>

			<v-select v-model="modul.tip" :items="moduleTypes" item-text="label" item-value="value"
				class="tip-select flex-grow-0" label="Tip modul" placeholder="Selecteaza" :rules="[rules.required]"
				dense outlined hide-details="auto" @change="setValues()"></v-select>

			<v-select v-if="variants.length > 0" v-model="modul.tip_poarta_mare" :items="variants" item-text="label"
				item-value="value" class="w-48 flex-grow-0" label="Varianta" placeholder="Selecteaza"
				:rules="[rules.required]" dense outlined hide-details="auto" @change="setValues()"></v-select>

			<!-- same width as the model select it stands in for, so the header does not jump -->
			<v-select v-if="isPost" v-model="modul.tip_stalp" :items="postSizes" class="w-40 flex-grow-0" label="Stalp"
				placeholder="Selecteaza" suffix="mm" :rules="[rules.required]" dense outlined hide-details="auto"
				@change="setValues()"></v-select>

			<v-select v-if="!isPost" v-model="modul.model" :items="modelOptions" item-text="name" item-value="code"
				class="w-40 flex-grow-0"
				label="Model gard" placeholder="Selecteaza" :rules="[rules.required]" dense outlined hide-details="auto"
				@change="onModelChange"></v-select>

			<template v-if="isCustomModel">
				<v-select v-model="modul.orientare" :items="orientationOptions" item-text="label" item-value="value"
					class="w-32 flex-grow-0" label="Directie" placeholder="Alege" :rules="[rules.required]" dense
					outlined hide-details="auto" @change="setValues()"></v-select>
				<v-select :value="customWidth" :items="slatWidthOptions" class="w-32 flex-grow-0" label="Lamela"
					placeholder="Alege" suffix="mm" :rules="[rules.required]" dense outlined hide-details="auto"
					@change="setCustomWidth"></v-select>
				<NumberField v-model="modul.min_gol" label="Gol min" suffix="mm" width-class="w-32"
					:rules="[rules.number]" />
			</template>

			<span v-if="!isPost && currentModel" class="text-sm text-gray-500">
				Lamele {{ slatWidthsText }} mm &middot; Gol min {{ minGap }} mm
			</span>

			<v-spacer></v-spacer>

			<NumberField v-model="modul.buc" label="Buc" :min="1" width-class="w-24" />

			<v-btn text small class="flex-grow-0" title="Dupleaza modulul" @click="$emit('duplicate')">
				<v-icon small left>mdi-content-copy</v-icon>
				duplică
			</v-btn>
			<v-btn icon small class="flex-grow-0 delete-modul" title="Sterge modulul" @click="$emit('delete')">
				<v-icon>mdi-delete-outline</v-icon>
			</v-btn>
		</div>

		<!-- anything wrong with the module, full width so it cannot be missed -->
		<div v-if="hasAlerts" class="px-3 pt-3">
			<v-alert v-model="visibleSlidingAlert" type="warning" outlined dense dismissible class="mb-2">
				<span class="font-bold">Atentie.</span>
				<span class="block sm:inline">
					Poarta culisanta necesita 500 mm extra lungime fata de golul dintre stalpi.
				</span>
			</v-alert>
			<v-alert v-model="visibleMinAreaAlert" type="warning" outlined dense dismissible class="mb-2">
				<span class="font-bold">Atentie.</span>
				<span class="block sm:inline">
					Elementul are un cost aditional de pana la <span class="font-bold">30%</span> pentru ca e sub
					suprafata limita de <span class="font-bold">{{ minArea }} m²</span>.
				</span>
			</v-alert>
			<v-alert v-if="isLegacyModel" type="info" outlined dense class="mb-2">
				<span class="font-bold">Model vechi.</span>
				<span class="block sm:inline">
					Acest modul a fost salvat cu modelul <span class="font-bold">{{ modul.denumire_model ||
						modul.model }}</span>, care nu mai exista in lista.
					Pretul salvat este pastrat; alege un model nou pentru recalculare.
				</span>
			</v-alert>
			<v-alert v-if="isOldFormat" type="info" outlined dense class="mb-2">
				<span class="font-bold">Format vechi.</span>
				<span class="block sm:inline">
					L si H sunt dimensiunile modulului, fara goluri sus/jos si fara dimensiuni calculate.
					Alege un model ca sa fie tratate ca gol de acoperit.
				</span>
			</v-alert>
			<v-alert v-model="visibleConvertedAlert" type="warning" outlined dense dismissible class="mb-2">
				<span class="font-bold">Verifica L si H.</span>
				<span class="block sm:inline">
					Modulul a fost convertit: L si H reprezinta acum golul de acoperit, nu modulul.
					<template v-if="assembly.adjustment">
						Se adauga automat {{ assembly.adjustment }} mm la latime &mdash; scade-i din L daca
						erau deja inclusi.
					</template>
				</span>
			</v-alert>
			<v-alert v-if="hasUnknownAdjustment" type="warning" outlined dense class="mb-2">
				<span class="font-bold">Latime nedefinita.</span>
				<span class="block sm:inline">
					Pentru poarta culisanta articulata nu e definit inca surplusul de latime fata de gol.
					Modulul e calculat la dimensiunea golului &mdash; verifica manual.
				</span>
			</v-alert>
			<v-alert v-if="needsVerticalReinforcement" type="warning" outlined dense class="mb-2">
				<span class="font-bold">Ranforsare verticala necesara.</span>
				<span class="block sm:inline">
					Canatul are {{ framedLeafWidth }} mm, peste {{ verticalSpacing }} mm: recomandat
					{{ suggestedVerticalCount }} {{ suggestedVerticalCount == 1 ? 'ranforsare verticala' : 'ranforsari verticale' }}
					pe canat.
				</span>
			</v-alert>
			<v-alert v-if="isMissingGeometry" type="warning" outlined dense class="mb-2">
				<span class="font-bold">Model fara geometrie.</span>
				<span class="block sm:inline">
					Modelul <span class="font-bold">{{ modul.model }}</span> nu are lamele setate.
					Completeaza-le in Setari &gt; Preturi garduri HUB &gt; Ajustari modele.
				</span>
			</v-alert>
		</div>

		<div class="flex flex-row items-stretch">
			<div class="flex-grow p-3 flex flex-col gap-3">
				<div class="border rounded p-3">
					<h6 class="text-xs uppercase tracking-wider text-gray-500 mb-3">Gol de acoperit</h6>
					<div class="flex flex-wrap items-start gap-2">
						<NumberField v-if="!isPost" v-model="modul.latime" label="L" suffix="mm" width-class="w-32"
							:rules="[rules.dimension]" />
						<NumberField v-model="modul.inaltime" label="H" suffix="mm" width-class="w-32"
							:rules="[rules.dimension]" />
						<NumberField v-if="hasClearanceGaps && isOpeningBased" v-model="modul.gol_sus" label="Gol sus" suffix="mm"
							:min="0" width-class="w-40" />
						<NumberField v-if="hasClearanceGaps && isOpeningBased" v-model="modul.gol_jos" label="Gol jos" suffix="mm"
							:min="0" width-class="w-40" />
					</div>
				</div>

				<div v-if="showAssembly" class="border rounded p-3 bg-gray-50">
					<h6 class="text-xs uppercase tracking-wider text-gray-500 mb-3">Dimensiuni calculate</h6>
					<div class="flex flex-row flex-wrap gap-x-8 gap-y-2 text-sm">
						<div>
							<div class="text-gray-500">Modul</div>
							<div class="font-bold">{{ assembly.module.width }} x {{ assembly.module.height }} mm</div>
						</div>
						<div v-if="assembly.known">
							<div class="text-gray-500">Poarta</div>
							<div class="font-bold">{{ leafWidthText }} x {{ assembly.leaf.height }} mm</div>
						</div>
						<div v-if="assembly.postHeight !== null">
							<div class="text-gray-500">H Toc</div>
							<div class="font-bold">{{ assembly.postHeight }} mm</div>
						</div>
						<div v-if="slatSpaceText">
							<div class="text-gray-500">Spatiu lamele</div>
							<div class="font-bold">{{ slatSpaceText }}</div>
						</div>
						<div v-if="assemblyBreakdown" class="bg-gray-200 p-2 text-gray-500 self-end">
							{{ assemblyBreakdown }}
						</div>
					</div>
				</div>

				<div v-if="!isPost" class="border rounded p-3">
					<h6 class="text-xs uppercase tracking-wider text-gray-500 mb-3">Configurare lamele</h6>
					<div class="flex flex-wrap items-start gap-2">
						<v-select v-model="modul.nr_profile" :items="slatCountChoices" class="w-36 flex-grow-0"
							label="Nr. profile" placeholder="Alege" :disabled="slatCountChoices.length < 1"
							:rules="[rules.required]" dense outlined hide-details="auto"></v-select>
						<v-text-field :value="distanta_gol" class="w-32 flex-grow-0" label="Gol calculat" suffix="mm"
							dense outlined readonly hide-details="auto"></v-text-field>
						<NumberField v-model="modul.gol_afisat" label="Gol afisat" suffix="mm" width-class="w-40" />
						<v-select v-if="hasReinforcement" v-model="modul.ranforsare" :items="reinforcementOptions"
							item-text="label" item-value="value" class="w-32 flex-grow-0" label="Ranforsare" dense
							outlined hide-details="auto"></v-select>
						<v-select v-if="hasVerticalReinforcement" v-model="modul.ranforsari_verticale"
							:items="verticalReinforcementOptions" class="w-44 flex-grow-0" label="Ranforsari verticale"
							suffix="/ canat" dense outlined hide-details="auto"></v-select>
					</div>
					<div v-if="slatAlternatives.length > 0" class="flex flex-row flex-wrap items-center gap-2 mt-3">
						<span class="text-sm text-gray-500">alternative:</span>
						<v-chip v-for="option in slatAlternatives" :key="option.count" small
							:color="option.count == modul.nr_profile ? 'teal' : ''"
							:text-color="option.count == modul.nr_profile ? 'white' : ''"
							@click="modul.nr_profile = option.count">
							{{ option.count }} &middot; gol {{ option.gap }}
						</v-chip>
					</div>
				</div>

				<div v-if="hasReinforcement">
					<div v-if="modul.pp_included" class="flex flex-row items-center gap-2">
						<span class="text-sm font-bold">Poarta pietonala integrata</span>
						<NumberField v-model="modul.latime_pp" label="L" suffix="mm" :disabled="!hasDimensions"
							width-class="w-32" />
						<v-btn icon small title="Elimina poarta pietonala" @click="closeFormPP()">
							<v-icon small>mdi-close</v-icon>
						</v-btn>
					</div>
					<v-btn v-else class="uppercase" small outlined :disabled="!hasDimensions" @click="openFormPP()">
						<v-icon left small>mdi-plus</v-icon>
						Poarta pietonala integrata
					</v-btn>
				</div>
			</div>

			<div class="w-64 flex-grow-0 bg-gray-100 px-5 py-4 flex flex-col gap-3">
				<div class="flex flex-row justify-between items-baseline">
					<span class="text-sm text-gray-600">{{ isPost ? 'Stalp' : 'Suprafata / buc' }}</span>
					<span class="font-bold text-lg">{{ isPost ? postLabel : suprafata_text }}</span>
				</div>
				<div class="flex flex-row justify-between items-baseline">
					<span class="text-sm text-gray-600">Pret / buc</span>
					<span class="font-bold text-lg">{{ isNaN(cost) ? '-' : cost }}</span>
				</div>
				<NumberField v-model="modul.marja" label="Marja" suffix="%" :step="0.1" width-class="w-full" />
				<div class="flex flex-row justify-between items-baseline">
					<span class="text-sm text-gray-600">Pret (€)</span>
					<span class="font-bold text-lg">{{ isNaN(cost_total) ? '-' : cost_total }}</span>
				</div>
				<div class="flex flex-row justify-between items-baseline">
					<span class="text-sm text-gray-600">Valoare (€)</span>
					<span class="font-bold text-2xl">{{ isNaN(total) ? '-' : total }}</span>
				</div>
			</div>
		</div>
	</v-form>
</template>

<script>
import {
	slatGap,
	maxSlatCount,
	minSlatCount,
	slatWidthLabel,
	isHorizontal,
	modelImageSlug,
	opensWithGap
} from "@/utils/fenceGeometry";
import {
	moduleUnitPrice,
	minAreaFor,
	smallElementRatio
} from "@/utils/fencePricing";
import {
	MODULE_TYPES,
	LEGACY_MODULE_TYPES,
	POST_SIZES,
	FRAME_THICKNESS,
	variantsFor,
	hasFrame,
	hasReinforcement,
	REINFORCEMENT_WIDTH,
	VERTICAL_REINFORCEMENT_SPACING,
	suggestedVerticalReinforcements,
	framedLeafCount,
	isPost,
	imageKind,
	CUSTOM_MODEL,
	isCustomModel,
	customModelCode,
	hasClearanceGaps,
	moduleAssembly,
	assemblyFor
} from "@/utils/fenceModules";
import NumberField from "@/components/Productie/Garduri/NumberField";

export default {
	name: "EditModul",
	data() {
		return {
			visibleSlidingAlert: false,
			visibleMinAreaAlert: false,
			visibleConvertedAlert: false,
			rules: {
				required: value => (value !== "" && value !== null && value !== undefined)
					|| "Camp obligatoriu",
				dimension: value => (!isNaN(parseFloat(value)) && parseFloat(value) > 0)
					|| "Valoare mai mare ca 0",
				// a min gap is signed: 0 means the slats touch, negative that they overlap
				number: value => !isNaN(parseFloat(value)) || "Valoare invalida",
			},
		};
	},
	components: { NumberField },
	props: ["modul", "country", "username", "position"],
	computed: {
		pricing() {
			return this.$store.state.hubFencePricing;
		},
		/**
		 * A retired type stays in the list only while a module still carries it, so an old offer
		 * can be opened without the select falling blank - and without being silently converted
		 * to a new type, which would reprice it.
		 */
		moduleTypes() {
			const retired = LEGACY_MODULE_TYPES.filter(type => type.value == this.modul.tip);

			return MODULE_TYPES.concat(retired);
		},
		variants() {
			return variantsFor(this.modul.tip);
		},
		postSizes() {
			return POST_SIZES;
		},
		reinforcementOptions() {
			// values stay strings: the layout reads "false" and Number(ranforsare) off the module
			return [
				{ label: "Fara", value: "false" },
				{ label: FRAME_THICKNESS + " mm", value: String(FRAME_THICKNESS) }
			];
		},
		hasAlerts() {
			return this.visibleSlidingAlert || this.visibleMinAreaAlert || this.visibleConvertedAlert
				|| this.isOldFormat || this.isLegacyModel
				|| this.isMissingGeometry || this.hasUnknownAdjustment || this.needsVerticalReinforcement;
		},
		/**
		 * Every buildable count, densest first. Bounded at both ends: the minimum gap rules out
		 * the dense end, the widest sensible gap the sparse one - two slats over a 1000 mm panel
		 * fit, but leave a gap nobody would build.
		 *
		 * A saved module keeps its own count even if it now falls outside that range, so an old
		 * offer opens on the count it was priced with instead of a blank select.
		 */
		/**
		 * The most slats that fit at the model's minimum gap. Kept apart from the list below,
		 * which also carries a saved count that no longer fits, so this stays the honest bound.
		 */
		slatCountMax() {
			if (this.isPost) return 0;

			return maxSlatCount(
				this.slatWidths, this.span, this.minGap, this.gapOffset, this.reinforcement
			);
		},
		slatCountChoices() {
			const fitting = this.slatCountMax;

			if (fitting < 1) return [];

			const sparsest = Math.min(
				fitting,
				minSlatCount(this.slatWidths, this.span, this.gapOffset, this.reinforcement)
			);
			const counts = [];

			for (let count = fitting; count >= sparsest; count--) {
				counts.push(count);
			}

			const saved = Number(this.modul.nr_profile);

			if (saved > 0 && counts.indexOf(saved) < 0) {
				counts.push(saved);
				counts.sort((a, b) => b - a);
			}

			return counts;
		},
		/**
		 * The three counts around the current pick, with the gap each would give. Clamped to what
		 * is buildable, so a shortcut can never land on a count that breaks the minimum gap.
		 */
		slatAlternatives() {
			const choices = this.slatCountChoices;

			if (choices.length < 1) return [];

			const densest = choices[0];
			const sparsest = choices[choices.length - 1];
			const current = Number(this.modul.nr_profile) || densest;
			const last = Math.min(densest, Math.max(current + 1, sparsest + 2));
			const first = Math.max(sparsest, last - 2);
			const options = [];

			for (let count = first; count <= last; count++) {
				const gap = slatGap(this.slatWidths, count, this.span, this.gapOffset, this.reinforcement);
				options.push({ count: count, gap: parseFloat(gap).toFixed(2) });
			}

			return options;
		},
		/** Articulated sliding has no confirmed allowance yet, so the card refuses to invent one. */
		hasUnknownAdjustment() {
			return this.isOpeningBased && this.assembly.unknown;
		},
		/** The integrated pedestrian gate needs the gate sized first. */
		hasDimensions() {
			return Number(this.modul.latime) > 0 && Number(this.modul.inaltime) > 0;
		},
		isPost() {
			return isPost(this.modul.tip);
		},
		hasFrame() {
			return hasFrame(this.modul.tip);
		},
		hasReinforcement() {
			return hasReinforcement(this.modul.tip);
		},
		/** A post's length in metres - the quantity its price is charged against. */
		postLength() {
			return (Number(this.priceSize.height) || 0) / 1000;
		},
		/** The post's cross-section and its length, since both drive what it costs. */
		postLabel() {
			const size = this.modul.tip_stalp ? this.modul.tip_stalp + " mm" : "-";

			return this.postLength > 0 ? `${size} · ${this.postLength.toFixed(2)} ml` : size;
		},
		models() {
			return this.pricing.models;
		},
		/** The model behind this module, or nothing if it was saved before the model list existed. */
		currentModel() {
			return this.models.find(model => model.code == this.modul.model);
		},
		/** The catalogue plus "Custom", for a design the twenty do not cover. */
		modelOptions() {
			return this.models.concat([{ code: CUSTOM_MODEL, name: "Custom" }]);
		},
		isCustomModel() {
			return isCustomModel(this.modul.model);
		},
		/**
		 * A custom module has no code, so its direction and width stand in for one - which lets
		 * every rule that reads a code (which way the slats run, whether the run opens with a gap,
		 * the image name) treat it as the catalogue model it resembles.
		 */
		effectiveModelCode() {
			return this.isCustomModel
				? customModelCode(this.modul.orientare, this.customWidth)
				: this.modul.model;
		},
		customWidth() {
			return Number(this.modul.slat_widths && this.modul.slat_widths[0]) || 0;
		},
		/** Every slat width the catalogue uses, so a custom module stays buildable from stock. */
		orientationOptions() {
			return [
				{ value: "H", label: "Orizontal" },
				{ value: "V", label: "Vertical" }
			];
		},
		slatWidthOptions() {
			const widths = [];

			this.models.forEach(model => {
				(model.slatWidths || []).forEach(width => {
					if (widths.indexOf(width) < 0) widths.push(width);
				});
			});

			return widths.sort((a, b) => a - b);
		},
		isLegacyModel() {
			return !!this.modul.model && !this.currentModel && !this.isCustomModel;
		},
		/**
		 * Only a model with no slat widths is unusable. The minimum gap is a real value at 0 (slats
		 * touching) and below it (slats overlapping, which is how the 87 models are built), so it
		 * says nothing about whether the model has been set up.
		 */
		isMissingGeometry() {
			if (this.isCustomModel) return false;

			return !!this.currentModel && this.currentModel.slatWidths.length < 1;
		},
		/**
		 * Geometry from the model, falling back to what the module was saved with so a legacy
		 * offer still lays out and prices the way it did.
		 */
		slatWidths() {
			if (this.isCustomModel) return this.customWidth > 0 ? [this.customWidth] : [];
			if (this.currentModel) return this.currentModel.slatWidths;

			return this.modul.slat_widths && this.modul.slat_widths.length > 0
				? this.modul.slat_widths
				: [Number(this.modul.latime_profil)].filter(width => width > 0);
		},
		minGap() {
			// a custom module states its own, there being no model to read it from
			if (this.isCustomModel) return Number(this.modul.min_gol) || 0;

			return this.currentModel ? this.currentModel.minGap : Number(this.modul.min_gol) || 0;
		},
		slatWidthsText() {
			return slatWidthLabel(this.slatWidths);
		},
		hasClearanceGaps() {
			return hasClearanceGaps(this.modul.tip);
		},
		/**
		 * Modules added from now on record an opening and the card derives the module from it.
		 * Anything saved earlier holds the module dimensions directly - an agent had already added
		 * the sliding allowance by hand - so those are taken as they are rather than adjusted a
		 * second time.
		 */
		isOpeningBased() {
			return !!this.modul.opening_based;
		},
		/** Saved before the opening refactor, typically arriving through a duplicated offer. */
		isOldFormat() {
			return !this.isOpeningBased && !this.isPost;
		},
		/** A module saved before the opening refactor holds its dimensions directly. */
		moduleSize() {
			return {
				width: Number(this.modul.latime) || 0,
				height: Number(this.modul.inaltime) || 0
			};
		},
		/**
		 * The module broken into its parts. Only meaningful once an opening is entered, and only
		 * fully broken down for the types whose assembly is specified.
		 */
		assembly() {
			return moduleAssembly(
				this.modul.tip,
				this.modul.tip_poarta_mare,
				this.modul.latime,
				this.modul.inaltime,
				{ top: this.modul.gol_sus, bottom: this.modul.gol_jos }
			);
		},
		/**
		 * What the offer charges for: the module across its full width, posts included, but only
		 * as tall as the gate itself.
		 *
		 * The posts run higher than that - they stand on the ground, so they span the clearance
		 * underneath - and the area deliberately ignores it. A post is 70 mm wide against a gate
		 * metres across, so the strip is worth a rounding error, while a client reading the offer
		 * expects the area to match the height they can see. Charging the taller figure is the
		 * harder thing for an agent to defend, so the module height is the one used.
		 */
		priceSize() {
			return this.isOpeningBased ? this.assembly.module : this.moduleSize;
		},
		/** What the slats are laid out inside: the leaf, which is narrower than the module. */
		layoutSize() {
			return this.isOpeningBased ? this.assembly.leaf : this.moduleSize;
		},
		/**
		 * The derived dimensions worth keeping on the module. Null where a type has no such part -
		 * a panel has no leaf distinct from itself, and nothing but a swing or pedestrian gate has
		 * posts.
		 */
		persistedDimensions() {
			const gate = this.isOpeningBased && this.assembly.known;

			return {
				modul_latime: this.priceSize.width || "",
				modul_inaltime: this.priceSize.height || "",
				poarta_latime: gate ? this.assembly.leaf.width : "",
				poarta_inaltime: gate ? this.assembly.leaf.height : "",
				toc_inaltime: gate ? this.assembly.postHeight : "",
				canate: gate ? this.assembly.leaves : "",
				spatiu_lamele: this.isPost || !(this.span > 0) ? "" : this.span
			};
		},
		showAssembly() {
			return this.isOpeningBased && !this.isPost && this.assembly.module.width > 0
				&& this.assembly.module.height > 0;
		},
		/**
		 * How the module width is spent, left to right, as the actual run of parts - so it can be
		 * checked against the module width by adding the line up.
		 *
		 * A gate that hangs between posts breaks into posts, clearances and leaves. A slider or a
		 * cantilever has none of those: its module is simply the opening plus the width it needs
		 * to run past it, which is the only thing there is to show.
		 */
		assemblyBreakdown() {
			const parts = assemblyFor(this.modul.tip, this.modul.tip_poarta_mare);

			if (!parts || !this.assembly.known) {
				const extra = this.assembly.adjustment;

				return extra ? `${Number(this.modul.latime) || 0} + ${extra} mm` : "";
			}

			const leaves = this.assembly.leaves;
			const each = this.leafWidth;
			const run = [parts.post, parts.gapStart];

			for (let leaf = 0; leaf < leaves; leaf++) {
				if (leaf > 0) run.push(parts.join);
				run.push(each);
			}

			run.push(parts.gapEnd, parts.post);

			return run.join(" + ") + " mm";
		},
		/**
		 * The run the slats are laid out in, and which way it runs: the leaf less its frame, over
		 * the height on an H model and over the width on a V model. Shown so a count and a gap can
		 * be checked by hand.
		 */
		slatSpaceText() {
			if (this.isPost || !this.modul.model || !(this.span > 0)) return "";

			return `${this.span} mm ${isHorizontal(this.effectiveModelCode) ? "(H)" : "(L)"}`;
		},
		/**
		 * One leaf's width. Not rounded: two leaves out of 2815 mm are 1407.5 each, and rounding
		 * that would stop the breakdown line adding back up to the module.
		 */
		leafWidth() {
			const exact = this.assembly.leaf.width / this.assembly.leaves;

			// trim the float noise a division leaves behind, keep any real fraction
			return Number(exact.toFixed(2));
		},
		/** "2 x 1407.5" for a multi-leaf gate, so each leaf's own width is visible. */
		leafWidthText() {
			return this.assembly.leaves > 1
				? `${this.assembly.leaves} x ${this.leafWidth}`
				: String(this.assembly.leaf.width);
		},
		suprafata() {
			let mp = (this.priceSize.width / 1000) * (this.priceSize.height / 1000);
			this.modul.suprafata = Number(mp);
			return this.modul.suprafata;
		},
		suprafata_pp() {
			let mp = (this.modul.latime_pp / 1000) * (this.priceSize.height / 1000);
			this.modul.suprafata_pp = Number(mp);
			return this.modul.suprafata_pp;
		},
		suprafata_text() {
			return this.suprafata.toFixed(2) + " m²";
		},
		/** The threshold this module is judged against, from the pricing config. */
		minArea() {
			return minAreaFor(this.pricing.pricingConfig && this.pricing.pricingConfig.minArea, this.modul.tip);
		},
		/**
		 * A single-leaf swing gate is priced per m² whatever its size, so it is never treated as a
		 * small element.
		 */
		isSmallElement() {
			if (!(this.suprafata > 0) || !(this.minArea > 0)) return false;
			if (this.modul.tip_poarta_mare == "batanta_simpla") return false;

			return this.suprafata < this.minArea;
		},
		/**
		 * Frame thickness per side. A saved offer keeps whatever it was built with; anything new
		 * gets the one thickness that exists.
		 */
		frameThickness() {
			const stored = Number(this.modul.grosime_rama);

			return stored > 0 ? stored : FRAME_THICKNESS;
		},
		/** Slats run across the height on H models and across the width on V models. */
		span() {
			// the slats sit inside the leaf, not the module: a swing or pedestrian gate loses a
			// post and a clearance each side, and an inter-leaf gap for every join
			const dimensiune = isHorizontal(this.effectiveModelCode)
				? this.layoutSize.height
				: this.layoutSize.width;

			// a framed module loses the frame on both sides before the slats start
			return this.hasFrame ? dimensiune - this.frameThickness * 2 : dimensiune;
		},
		/**
		 * A run that opens and closes with a gap has one more gap than slats; one that butts a
		 * slat against each edge has one fewer. Inside a gate it is always the former - the frame
		 * is already there - while a plain panel depends on how the model itself is built. A
		 * reinforcement profile sits in the run like another slat and adds one gap on top.
		 */
		gapOffset() {
			const opens = opensWithGap(this.effectiveModelCode, this.hasFrame) ? 1 : -1;

			return opens + this.reinforcementsInRun;
		},
		/** Width of the horizontal reinforcement profile, if the gate has one. */
		horizontalReinforcement() {
			if (!this.hasReinforcement) return 0;
			if (!this.modul.ranforsare || this.modul.ranforsare == "false") return 0;

			return Number(this.modul.ranforsare) || 0;
		},
		/**
		 * Reinforcements that sit in the slat run like another slat. Only the ones crossing the
		 * slats do: the horizontal one on an H model, the verticals on a V model - every leaf's,
		 * since a V model lays all its leaves out as one run. A reinforcement running alongside
		 * the slats takes nothing out of the run.
		 */
		reinforcementsInRun() {
			if (isHorizontal(this.effectiveModelCode)) return this.horizontalReinforcement > 0 ? 1 : 0;

			return this.verticalReinforcements * this.framedLeaves;
		},
		/** Width the reinforcements take out of the span. */
		reinforcement() {
			return isHorizontal(this.effectiveModelCode)
				? this.horizontalReinforcement
				: this.reinforcementsInRun * REINFORCEMENT_WIDTH;
		},
		/** Vertical reinforcement goes where the horizontal one does - driveway gates - on a vertical model. */
		hasVerticalReinforcement() {
			return this.hasReinforcement && !!this.effectiveModelCode && !isHorizontal(this.effectiveModelCode);
		},
		framedLeaves() {
			return framedLeafCount(this.modul.tip_poarta_mare);
		},
		/** One framed leaf's width: the leaf on a gate between jambs, a share of the module on a slider. */
		framedLeafWidth() {
			return Number((this.layoutSize.width / this.framedLeaves).toFixed(2));
		},
		verticalSpacing() {
			return VERTICAL_REINFORCEMENT_SPACING;
		},
		suggestedVerticalCount() {
			return this.hasVerticalReinforcement ? suggestedVerticalReinforcements(this.framedLeafWidth) : 0;
		},
		verticalReinforcements() {
			if (!this.hasVerticalReinforcement) return 0;

			return Math.max(0, parseInt(this.modul.ranforsari_verticale) || 0);
		},
		verticalReinforcementOptions() {
			const last = Math.max(2, this.suggestedVerticalCount + 1, this.verticalReinforcements);
			const options = [];

			for (let count = 0; count <= last; count++) options.push(count);

			return options;
		},
		/** Production's 1700 mm rule is a recommendation; the card only says when it is not met. */
		needsVerticalReinforcement() {
			return this.verticalReinforcements < this.suggestedVerticalCount;
		},
		distanta_gol() {
			const gol = slatGap(
				this.slatWidths,
				Number(this.modul.nr_profile),
				this.span,
				this.gapOffset,
				this.reinforcement
			);

			this.modul.distanta_gol = parseFloat(gol).toFixed(2);
			return this.modul.distanta_gol;
		},
		cost() {
			let t1;
			let t2 = 0;

			// a post is sold by the metre, so its height is the quantity - raise the height and the
			// price follows. There is no area to scale by and no small-element threshold to fall under.
			if (this.isPost) {
				t1 = Number(this.modul.pret) * this.postLength;
			}
			// a module saved before the model list existed is priced whole off its stored price,
			// the way it was when it was saved
			else if (this.currentModel && this.modul.pp_included) {
				const config = this.pricing.pricingConfig && this.pricing.pricingConfig.minArea;
				const leafArea = Number(this.suprafata) - Number(this.suprafata_pp);

				// the two halves are judged against their own thresholds and priced separately
				const raport_pp = smallElementRatio(this.suprafata_pp, minAreaFor(config, "Poarta_mica"));
				const raport_pa = smallElementRatio(
					leafArea,
					minAreaFor(config, "Poarta_mare", { pedestrianIncluded: true })
				);

				t1 = leafArea * Number(this.modul.pret) * raport_pa;
				t2 = this.suprafata_pp * this.pedestrianPricePerSqm * raport_pp;
			} else {
				const raport = this.isSmallElement ? smallElementRatio(this.suprafata, this.minArea) : 1;

				t1 = Number(this.suprafata) * Number(this.modul.pret) * raport;
			}

			const cost_modul = (Number(t1) + Number(t2)).toFixed(2);
			this.modul.cost_modul = Number(cost_modul);
			return this.modul.cost_modul;
		},
		/** An integrated pedestrian gate is priced off its own base price, not the gate's. */
		pedestrianPricePerSqm() {
			return moduleUnitPrice({
				basePrices: this.pricing.basePrices,
				country: this.country,
				tip: "Poarta_mica",
				adjustment: this.modelAdjustment
			});
		},
		modelAdjustment() {
			const adjustments = this.pricing.modelAdjustments;

			return adjustments && this.modul.model ? adjustments[this.modul.model] : 0;
		},
		cost_total() {
			let t = (this.cost * this.modul.buc).toFixed(2);
			this.modul.cost_total = Number(t);
			return t;
		},
		total() {
			let c = Number(this.cost_total);
			let t = (c + c * (this.modul.marja / 100)).toFixed(2);
			this.modul.pret_modul = Number(t);
			return t;
		},
	},
	watch: {
		"modul.tip"(newVal, oldVal) {
			if (newVal != oldVal) {
				// every control below the type is scoped to a type, so a leftover value would keep
				// applying to whatever the module became: the variant sets differ per gate family,
				// the reinforcement would keep eating into the span, and the post size would keep
				// naming the price column
				this.modul.tip_poarta_mare = null;
				this.modul.ranforsare = "false";
				this.$set(this.modul, "ranforsari_verticale", 0);
				this.$set(this.modul, "tip_stalp", null);

				// a post belongs to no fence model, so a leftover one would follow it onto the offer
				if (isPost(newVal)) {
					this.modul.model = "";
					this.modul.denumire_model = "";
				}

				if (!hasReinforcement(newVal)) {
					this.modul.pp_included = false;
					this.modul.latime_pp = "";
				}

				this.visibleSlidingAlert = false;
			}
		},
		// an H model or a non-gate has nowhere to put them, and a leftover count would still be
		// read off the saved module by the materials bill
		hasVerticalReinforcement(allowed) {
			if (!allowed && Number(this.modul.ranforsari_verticale) > 0) {
				this.modul.ranforsari_verticale = 0;
			}
		},
		"modul.pp_included"(newVal, oldVal) {
			if (newVal != oldVal) {
				this.setValues();
			}
		},
		"modul.tip_poarta_mare"(newVal, oldVal) {
			if (newVal != oldVal) {
				if (newVal == 'batanta_simpla' || oldVal == 'batanta_simpla') {
					this.modul.latime == ""
				}
			}
		},
		/**
		 * Keep the slat count on something buildable.
		 *
		 * Fills in when nothing is chosen, and pulls back to the densest that fits when the choice
		 * is too dense for the model's minimum gap. Adding a reinforcement is the usual way that
		 * happens: the profile takes 70 mm out of the span and adds a gap, so a count that was at
		 * the maximum a moment ago now needs a gap narrower than the model allows - negative,
		 * where the minimum is 0.
		 *
		 * Watched on the bound rather than on the list of choices, because the list deliberately
		 * carries a saved count that no longer fits so the select never falls blank. A count that
		 * is merely sparser than the maximum is left alone - it is wider than needed, not
		 * impossible - so choosing fewer slats survives an unrelated edit.
		 */
		slatCountMax(max) {
			if (max < 1) return;

			const current = Number(this.modul.nr_profile);

			if (!current || current > max) {
				this.modul.nr_profile = max;
			}
		},
		/**
		 * Persist the derived dimensions onto the module.
		 *
		 * The offer screen and the PDF read the saved module, not this card, so anything they show
		 * has to be stored rather than recomputed - and recomputing later would give a different
		 * answer anyway once a model's geometry is edited in Setari. `latime` / `inaltime` stay
		 * the opening the agent measured; these are what gets built from it.
		 */
		persistedDimensions: {
			handler(dimensions) {
				Object.keys(dimensions).forEach(field => {
					this.$set(this.modul, field, dimensions[field]);
				});
			},
			immediate: true
		},
		isSmallElement: {
			handler(newVal) {
				this.visibleMinAreaAlert = newVal;
			},
			immediate: true
		},
		// the price per m² is only stored on the module, so it has to be refreshed once the base
		// prices arrive for a module that was already on screen
		"pricing.basePrices"() {
			if (this.currentModel) this.recalculatePrice();
		},
	},
	created() {
		// a module from an offer saved before the field existed would otherwise get it non-reactive
		if (!("ranforsari_verticale" in this.modul)) {
			this.$set(this.modul, "ranforsari_verticale", 0);
		}
	},
	methods: {
		showAlert() {
			// a module that records its opening gets the sliding allowance added for it; only an
			// older one still needs the agent to add it by hand
			this.visibleSlidingAlert = !this.isOpeningBased && this.modul.tip_poarta_mare == "culisanta";
		},
		openFormPP() {
			this.modul.pp_included = true;
		},
		closeFormPP() {
			this.modul.latime_pp = ""
			this.modul.pp_included = false;
		},
		/**
		 * Offer images are named after the model code and the module type:
		 * h60-100-20_poarta_mare2_pp.png
		 */
		/**
		 * Offer images are named after the model and what the module is:
		 * h60-100-20_culisanta_telescopica_pp.png
		 *
		 * A post is the exception - it looks the same whatever fence it stands in, so it is named
		 * by its size alone and one image serves every model.
		 */
		imageName() {
			if (this.isPost) {
				return this.modul.tip_stalp ? `stalp_${this.modul.tip_stalp}.png` : "";
			}

			const kind = imageKind(this.modul.tip, this.modul.tip_poarta_mare);
			const pp = this.hasReinforcement && this.modul.pp_included ? "_pp" : "";

			// A custom design is whatever the client asked for, so there is no render of its slats
			// and never will be - the widths are open-ended. A generic set stands in: the module's
			// own shape, drawn once per direction. No _pp variants, so a gate with an integrated
			// pedestrian gate falls back to the plain picture of its kind.
			if (this.isCustomModel) {
				return this.modul.orientare
					? `custom_${String(this.modul.orientare).toLowerCase()}_${kind}.png`
					: "";
			}

			return `${modelImageSlug(this.effectiveModelCode)}_${kind}${pp}.png`;
		},
		setValues() {
			this.showAlert();

			if (!(this.hasReinforcement && this.modul.pp_included)) {
				this.modul.latime_pp = "";
			}

			// kept on the module because the offer is stored as a snapshot: the select is gone, but
			// a saved module should still say what frame it was built with
			this.$set(this.modul, "grosime_rama", this.hasFrame ? this.frameThickness : "");

			// a post is priced off its size alone, so its price must not wait on a model
			this.recalculatePrice();

			// A post has no model, so it would fall out at the model guard below and never be
			// given a picture. Its size is all the filename needs, and none of the model fields
			// after this point apply to it.
			if (this.isPost) {
				this.modul.filename = this.imageName();
				return;
			}

			if (this.isCustomModel) {
				// named the way the catalogue codes are, so a signed document records the design:
				// direction and slat width, e.g. "H87.4"
				this.modul.denumire_model = this.effectiveModelCode;
				this.modul.latime_profil = this.customWidth || "";
				this.modul.filename = this.imageName();
				return;
			}

			const model = this.currentModel;
			if (!model) return;

			this.modul.denumire_model = model.name;
			// $set because a module loaded from an offer saved before these fields existed would
			// otherwise get them as non-reactive properties
			this.$set(this.modul, "min_gol", model.minGap);
			// kept on the module so a saved offer keeps its layout even if the model changes later
			this.$set(this.modul, "slat_widths", model.slatWidths.slice());
			// single-width models keep a plain number here, the way the offer and the materials
			// bill have always read it; the three multi-width models carry the pattern instead
			this.modul.latime_profil = model.slatWidths.length == 1
				? model.slatWidths[0]
				: slatWidthLabel(model.slatWidths);
			this.modul.filename = this.imageName();
		},
		/**
		 * Picking a model is a deliberate re-quote, the same thing the legacy-model alert asks for,
		 * so an older module is moved onto the opening format at that point.
		 */
		onModelChange() {
			if (this.isOldFormat) this.adoptOpeningFormat();

			this.setValues();
		},
		/**
		 * Move a module saved before the opening refactor onto the opening format.
		 *
		 * Never done on load: an old module's L and H are the module itself - a slider already
		 * carries the allowance an agent added by hand - so reading them as an opening reprices it.
		 * L and H are kept as they are and the agent is told to check them.
		 */
		adoptOpeningFormat() {
			// $set because a module from an older offer has none of these keys, and a plain
			// assignment would leave them non-reactive
			this.$set(this.modul, "opening_based", true);
			if (!("gol_sus" in this.modul)) this.$set(this.modul, "gol_sus", "");
			if (!("gol_jos" in this.modul)) this.$set(this.modul, "gol_jos", "");

			this.visibleConvertedAlert = true;
			// the hand-added sliding allowance warning no longer applies
			this.showAlert();
		},
		/** The width lives in slat_widths, the same field a catalogue model fills. */
		setCustomWidth(width) {
			this.$set(this.modul, "slat_widths", width > 0 ? [Number(width)] : []);
			this.setValues();
		},
		/** Called by the parent before saving; showing the errors on this card is the point. */
		validate() {
			return this.$refs.form.validate();
		},
		recalculatePrice() {
			this.modul.pret = moduleUnitPrice({
				basePrices: this.pricing.basePrices,
				country: this.country,
				tip: this.modul.tip,
				variant: this.modul.tip_poarta_mare,
				postSize: this.modul.tip_stalp,
				adjustment: this.modelAdjustment
			});
		}
	},
};
</script>

<style scoped>
/* the icon carries Vuetify's own color, so tailwind on the button would not reach it */
.delete-modul:hover .v-icon {
	color: #ef4444;
}

/* the type select sizes itself to the selected label - the longest one is much longer than the
   rest, so a fixed width is either too tight or wastes the header's room */
.tip-select {
	width: auto;
	min-width: 17rem;
}

.tip-select ::v-deep .v-select__selections {
	flex-wrap: nowrap;
}

/* the readonly input would otherwise claim its own width on top of the label's */
.tip-select ::v-deep .v-select__selections input {
	width: 0;
	min-width: 0;
	padding: 0;
}

.tip-select ::v-deep .v-select__selection--comma {
	overflow: visible;
	text-overflow: clip;
	max-width: none;
}
</style>
